import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Avatar,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { FiChevronDown } from "react-icons/fi";
import moment from "moment-timezone";
import CDS from "design-system";
import { Message } from "features/Resources/Message/models";
import { setNewMessageInQueryClient, useUpdateMessage } from "features/Resources/Message/hooks";
import EditableScheduledFieldTag, { buildTimezoneDescription } from "features/Resources/Shared/Components/EditableScheduledFieldTag";
import { apiMessageById } from "helpers/url/api/v2";
import useAutoSave from "components/FormElements/useAutoSave";
import { useQueryClient } from "@tanstack/react-query";
import { COMMS_NO_ACTION_MENU_STATUSES, COMMS_NON_EDITABLE_STATUSES } from "features/Resources/Shared/constants";
import { removeLocalData } from "features/Resources/Shared/helpers";
import ActionHeader from "features/Resources/Shared/Components/ActionHeader";
import {
  ListIssuesByAction,
  ListIssuesByActionDuringPending,
} from "features/Issues/Components/ListIssues/ListIssuesByAction";
import TrixEditor from "components/FormElements/Fields/TrixTextAreaField/TrixEditor";
import ResourceOptionMenu from "features/Resources/Shared/Components/Button/ResourceOptionMenu";
import { useCurrentUserQuery, useUserDetailQuery } from "features/User/hooks";
import { useTaskDetailQuery } from "features/Resources/Task/hooks";
import UserOrChannelSelectField from "components/FormElements/Fields/SelectFields/UserOrChannelSelectField";
import { UserOrChannelResponse } from "models/v2/roleUserChannel";
import { toCompositeId } from "helpers/compositeId";

import NewHireStackedLabel from "features/NewHireJourney/Components/NewHireStackedLabel";
import { useNewHireJourneyDetailQuery } from "features/NewHireJourney/hooks";
import { titleize } from "helpers/string";
import ROLE from "models/role";
import RoleLabelTag from "components/Tag/UserTag/RoleLabelTag";
import ComposeBox, { useComposeBoxColors } from "features/Resources/Shared/Components/ComposeBox";

// Strip Trix's default border + horizontal padding so it sits flush inside the compose box.
const BORDERLESS_TRIX_SX = {
  ".trix-container trix-editor": {
    border: "none !important",
    borderRadius: "0 !important",
    padding: "0 !important",
    minHeight: "100px",
  },
  ".trix-container": {
    gap: "8px !important",
  },
  ".trix-button--icon-attach, .trix-button--icon-decrease-nesting-level, .trix-button--icon-increase-nesting-level, .trix-button--icon-undo, .trix-button--icon-redo": {
    display: "none !important",
  },
};

// Strip the recipient picker's outer border so it blends into the compose-box header.
const HEADER_PICKER_SX = {
  ".chakra-react-select__control": {
    border: "none !important",
    minHeight: "auto !important",
    background: "transparent !important",
    boxShadow: "none !important",
    paddingLeft: "0 !important",
  },
  ".chakra-react-select__value-container": {
    paddingLeft: "0 !important",
    paddingRight: "0 !important",
  },
  ".chakra-react-select__indicators": {
    display: "none",
  },
};

const MESSAGE_STATUS_COLORS: Record<string, string> = {
  pending: "blue.400",
  delivered: "green.500",
  failed: "red.500",
  skipped: "gray.400",
  removed: "gray.400",
};

const MESSAGE_STATUS_LABELS: Record<string, string> = {
  pending: "Unsent",
  delivered: "Delivered",
  failed: "Failed",
  skipped: "Skipped",
  removed: "Removed",
};

interface SenderSelectProps {
  form: ReturnType<typeof useForm<Message>>;
  isDisabled: boolean;
  originalSenderId?: string | null;
}

function SenderSelect({ form, isDisabled, originalSenderId }: SenderSelectProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { data: currentUser } = useCurrentUserQuery();
  const senderId = form.watch("senderId");
  const { data: sender } = useUserDetailQuery(senderId || "", { enabled: !!senderId });

  // Fetch the original sender — only when it's a third party (not me, not bot).
  const isOriginalOtherUser = !!originalSenderId && !!currentUser && originalSenderId !== currentUser.id;
  const { data: originalSender } = useUserDetailQuery(originalSenderId || "", { enabled: isOriginalOtherUser });

  // Track the pending action (Me or Bot) while the confirm dialog is open.
  const pendingAction = useRef<"me" | "bot" | null>(null);

  // Once confirmed away from the other-user, permanently hide that option.
  const [otherUserGone, setOtherUserGone] = useState(false);

  const isMyself = !!senderId && !!currentUser && senderId === currentUser.id;
  const isBot = !senderId;
  const isOtherUser = !isMyself && !isBot;

  const commitPendingAction = () => {
    setOtherUserGone(true);
    if (pendingAction.current === "me" && currentUser) {
      form.setValue("senderId", currentUser.id, { shouldDirty: true });
    } else if (pendingAction.current === "bot") {
      form.setValue("senderId", null as any, { shouldDirty: true });
    }
    pendingAction.current = null;
    onConfirmClose();
    onClose();
  };

  const handleMe = () => {
    if (isOtherUser) {
      pendingAction.current = "me";
      onConfirmOpen();
      return;
    }
    if (currentUser) form.setValue("senderId", currentUser.id, { shouldDirty: true });
    onClose();
  };

  const handleBot = () => {
    if (isOtherUser) {
      pendingAction.current = "bot";
      onConfirmOpen();
      return;
    }
    form.setValue("senderId", null as any, { shouldDirty: true });
    onClose();
  };

  const handleOtherUser = () => {
    if (originalSenderId) form.setValue("senderId", originalSenderId, { shouldDirty: true });
    onClose();
  };

  const showOtherUserOption = isOriginalOtherUser && !!originalSender && !otherUserGone;
  const displayName = (isOtherUser ? sender?.fullName : null) ?? (isBot ? "Camino Bot" : (currentUser?.fullName ?? "Me"));

  const trigger = (
    <HStack
      spacing={2}
      px={3}
      py={1.5}
      borderWidth={1}
      borderColor="gray.200"
      borderRadius="md"
      bg="white"
      cursor={isDisabled ? "default" : "pointer"}
      _hover={isDisabled ? {} : { bg: "gray.50" }}
      minW="220px"
    >
      <Avatar
        name={displayName}
        src={isBot ? undefined : (sender?.avatarUrl ?? currentUser?.avatarUrl ?? undefined)}
        size="xs"
        flexShrink={0}
      />
      <Text fontSize="sm" fontWeight="medium" color="content.primary" flex={1} noOfLines={1}>
        {isMyself ? "Me" : displayName}
      </Text>
      {!isDisabled && <Box as={FiChevronDown} boxSize={3.5} color="content.tertiary" />}
    </HStack>
  );

  if (isDisabled) return trigger;

  return (
    <>
      <Popover placement="bottom-start" isLazy isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
        <PopoverTrigger>{trigger}</PopoverTrigger>
      <PopoverContent w="300px">
        <PopoverBody p={1.5}>
          <VStack spacing={0} align="stretch">
            <HStack
              px={3}
              py={2}
              cursor="pointer"
              borderRadius="md"
              bg={isMyself ? "blue.50" : "transparent"}
              _hover={{ bg: isMyself ? "blue.50" : "gray.50" }}
              onClick={handleMe}
              spacing={2}
            >
              <Avatar name={currentUser?.fullName ?? ""} src={currentUser?.avatarUrl ?? undefined} size="xs" />
              <Text fontSize="sm" fontWeight={isMyself ? "700" : "normal"} color="content.primary" flex={1}>
                Me
              </Text>
              {isMyself && <Box w="6px" h="6px" borderRadius="full" bg="blue.400" flexShrink={0} />}
            </HStack>
            <HStack
              px={3}
              py={2}
              cursor="pointer"
              borderRadius="md"
              bg={isBot ? "blue.50" : "transparent"}
              _hover={{ bg: isBot ? "blue.50" : "gray.50" }}
              onClick={handleBot}
              spacing={2}
            >
              <Avatar name="Camino Bot" size="xs" />
              <Text fontSize="sm" fontWeight={isBot ? "700" : "normal"} color="content.primary" flex={1}>
                Camino Bot
              </Text>
              {isBot && <Box w="6px" h="6px" borderRadius="full" bg="blue.400" flexShrink={0} />}
            </HStack>
            {showOtherUserOption && (
              <>
                <Divider my={1.5} />
                <Box px={2} pb={1}>
                  <Text fontSize="xs" color="content.tertiary" fontWeight="500" mb={1.5}>
                    Send as another user
                  </Text>
                  <HStack
                    px={2}
                    py={1.5}
                    cursor="pointer"
                    borderRadius="md"
                    bg={isOtherUser ? "blue.50" : "transparent"}
                    _hover={{ bg: isOtherUser ? "blue.50" : "gray.50" }}
                    onClick={handleOtherUser}
                    spacing={2}
                  >
                    <Avatar
                      name={originalSender.fullName}
                      src={originalSender.avatarUrl ?? undefined}
                      size="xs"
                    />
                    <Text fontSize="sm" fontWeight={isOtherUser ? "700" : "normal"} color="content.primary" flex={1}>
                      {originalSender.fullName}
                    </Text>
                    {isOtherUser && <Box w="6px" h="6px" borderRadius="full" bg="blue.400" flexShrink={0} />}
                  </HStack>
                </Box>
              </>
            )}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>

    <AlertDialog isOpen={isConfirmOpen} leastDestructiveRef={cancelRef} onClose={onConfirmClose} isCentered>
      <AlertDialogOverlay>
        <AlertDialogContent borderRadius="xl" maxW="sm" py={2}>
          <AlertDialogHeader fontSize="md" fontWeight="bold">
            Change sender?
          </AlertDialogHeader>
          <AlertDialogBody fontSize="sm" color="fg.muted">
            {originalSender && (
              <>
                Once you switch away from
                {" "}
                <strong>{originalSender.fullName}</strong>
                , you can&apos;t go back. This message will no longer be sent as them.
              </>
            )}
          </AlertDialogBody>
          <AlertDialogFooter gap={2}>
            <Button ref={cancelRef} onClick={onConfirmClose} variant="ghost" size="sm">
              Cancel
            </Button>
            <Button colorScheme="red" size="sm" onClick={commitPendingAction}>
              Switch sender
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
    </>
  );
}

interface RecipientPickerProps {
  form: ReturnType<typeof useForm<Message>>;
  data: Message;
  isDisabled: boolean;
}

function RecipientPicker({ form, data, isDisabled }: RecipientPickerProps) {
  const recipientId = (useWatch({ control: form.control, name: "recipientId" }) as string | null | undefined) || "";
  const recipientRole = (useWatch({ control: form.control, name: "recipientRole" }) as string | null | undefined) || "";
  const { data: recipient } = useUserDetailQuery(recipientId, { enabled: !!recipientId });

  const initialCompositeId = data.recipientId && data.recipientType
    ? toCompositeId(data.recipientType, data.recipientId)
    : "";

  const pickerForm = useForm<{ recipientCompositeId: string[] }>({
    defaultValues: { recipientCompositeId: initialCompositeId ? [initialCompositeId] : [] },
  });
  const [isEditing, setIsEditing] = useState(false);

  // Drive form sync from react-select's change, NOT from a useWatch effect —
  // setIsEditing inside an effect would cascade renders.
  const handlePickerChange = (selected: UserOrChannelResponse | undefined) => {
    if (!selected) return;
    setIsEditing(false);
    const resourceId = selected.id;
    const resourceType = selected.recordType;
    if (!resourceId || !resourceType) return;

    const currentId = form.getValues("recipientId");
    const currentType = form.getValues("recipientType");
    const currentRole = form.getValues("recipientRole");
    if (resourceId === currentId && resourceType === currentType && !currentRole) return;

    form.setValue("recipientId", resourceId, { shouldDirty: true });
    form.setValue("recipientType", resourceType, { shouldDirty: true });
    if (currentRole) form.setValue("recipientRole", null as any, { shouldDirty: true });
  };

  // Toggle into editing mode WITHOUT touching the form — we only save when a new
  // recipient is picked. If the user cancels, the chip reverts to the original.
  const handleClearRecipient = () => {
    pickerForm.setValue("recipientCompositeId", []);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    pickerForm.setValue("recipientCompositeId", initialCompositeId ? [initialCompositeId] : []);
    setIsEditing(false);
  };

  if (isDisabled || (!isEditing && (!!recipient || !!recipientRole))) {
    return (
      <Box flex={1}>
        <RoleLabelTag
          user={recipient ? { fullName: recipient.fullName, avatarUrl: recipient.avatarUrl } : undefined}
          role={recipient ? undefined : (recipientRole || undefined)}
          size="md"
          bordered
          remove={!isDisabled && !!recipient ? handleClearRecipient : undefined}
        />
      </Box>
    );
  }

  return (
    <Box sx={HEADER_PICKER_SX} flex={1}>
      <UserOrChannelSelectField
        name="recipientCompositeId"
        control={pickerForm.control as any}
        errors={{}}
        variant="outline"
        formFieldProps={{ overrideHelperSpace: true }}
        types={["users", "channels"]}
        placeholder="Search users or channels…"
        isDisabled={isDisabled}
        components={{ DropdownIndicator: null, ClearIndicator: () => null }}
        onChangeCallback={handlePickerChange}
        onMenuClose={handleCancelEdit}
      />
    </Box>
  );
}

function SendAtFieldTag({ message }: { message: Message }) {
  const { data: dependencyTask } = useTaskDetailQuery(message.deliverAfterDependencyId, {
    enabled: !!message.deliverAfterDependencyId,
  });
  const { data: journey } = useNewHireJourneyDetailQuery(message.newHireJourneyId, {
    enabled: !!message.newHireJourneyId,
  });

  const { mutate: updateMessage, isPending: isSaving } = useUpdateMessage(message.id);
  const isEditable = !COMMS_NON_EDITABLE_STATUSES.includes(message.status || "");

  // Prefer message TZ (the field the backend uses to interpret deliver_at),
  // fall back to the new hire's TZ, then the browser TZ.
  const newHireTz = journey?.newHire?.timezone;
  const messageTz = message.timezone;
  const timezone = messageTz || newHireTz || moment.tz.guess();
  const timezoneDescription = buildTimezoneDescription({ timezone, resourceTz: messageTz, newHireTz });

  const handleSave = (iso: string) => {
    updateMessage({ deliverAt: iso } as Partial<Message> as Message);
  };

  // Once delivered, show the actual send time under a past-tense label.
  if (message.status === "delivered" && message.effectiveDeliverAt) {
    return (
      <CDS.FieldTag label="Sent">
        {moment.tz(message.effectiveDeliverAt, timezone).format("MMM D, YYYY [at] h:mm A")}
      </CDS.FieldTag>
    );
  }

  // Dependency-based scheduling stays read-only — editing means switching schedule mode.
  if (message.deliverAfterDependencyId) {
    const depName = dependencyTask?.name ?? "Dependency";
    return <CDS.FieldTag label="Scheduled">{`After ${depName}`}</CDS.FieldTag>;
  }

  const concreteIso = message.deliverAt
    || (message.deliverImmediately ? message.effectiveDeliverAt || journey?.pendingUpdatedAt : undefined);

  // Flag the pill red when a still-pending message is scheduled in the past —
  // the runner should have fired by now, so the user needs to act.
  const isPastDue = !!concreteIso
    && message.status === "pending"
    && moment.tz(concreteIso, timezone).isBefore(moment.tz(timezone));

  if (concreteIso) {
    return (
      <EditableScheduledFieldTag
        currentIso={concreteIso}
        timezone={timezone}
        timezoneDescription={timezoneDescription}
        displayText={moment.tz(concreteIso, timezone).format("MMM D, YYYY [at] h:mm A")}
        onSave={handleSave}
        isEditable={isEditable}
        isSaving={isSaving}
        tone={isPastDue ? "critical" : "default"}
      />
    );
  }

  if (message.deliverImmediately) {
    return (
      <EditableScheduledFieldTag
        timezone={timezone}
        timezoneDescription={timezoneDescription}
        displayText="Immediately"
        onSave={handleSave}
        isEditable={isEditable}
        isSaving={isSaving}
      />
    );
  }
  return null;
}

function TriggerFieldTag({ message }: { message: Message }) {
  if (!message.deliverAtDescriptor) return null;
  const label = message.forcedById ? "Manually Scheduled" : message.deliverAtDescriptor;
  return <CDS.FieldTag label="Send Logic">{label}</CDS.FieldTag>;
}

interface MessageFormProps {
  data: Message;
  includeHeader: boolean | undefined;
  includeMenu: boolean | undefined;
  isPending: boolean;
  includeStatus: boolean;
  includeTemplate?: boolean;
  includeJourney?: boolean;
}

export default function MessageForm({
  data,
  includeHeader = false,
  includeMenu = false,
  isPending,
  includeTemplate = false,
  includeJourney = true,
}: MessageFormProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUserQuery();
  const form = useForm<Message>({ defaultValues: data });

  const { bodyBg: composeBodyBg, bodyHoverBg: composeBodyHoverBg } = useComposeBoxColors();

  const isSent = COMMS_NON_EDITABLE_STATUSES.includes(data.status || "");
  const canEdit = !!currentUser && (
    currentUser.id === data.senderId
    || currentUser.permission?.includes(ROLE.PEOPLE_TEAM)
    || currentUser.permission?.includes(ROLE.COMPANY_ADMIN)
  );
  const isDisabled = isSent || !canEdit;
  const showActionMenu = !COMMS_NO_ACTION_MENU_STATUSES.includes(data.status || "");

  const transformData = (updateData: Partial<Message>) => ({ message: removeLocalData(updateData) });

  useAutoSave({
    form,
    transformData,
    submitUrl: apiMessageById(data?.id),
    onSuccess: (res: any, payload: any) => {
      setNewMessageInQueryClient(queryClient, res);
      // Body autosaves on every keystroke — skip the success toast so it doesn't
      // spam. Other fields (recipient, schedule, sender) are discrete actions
      // where a confirmation is useful.
      const updatedKeys = Object.keys(payload?.message ?? {});
      const onlyBody = updatedKeys.length > 0 && updatedKeys.every((k) => k === "body");
      if (!onlyBody) toast({ title: "Message updated.", status: "success" });
    },
  });

  return (
    <VStack spacing={1} align="stretch" width="100%">
      {includeHeader && (
        <HStack justify="space-between" mb={2}>
          <ActionHeader form={form} action={data} isDisabled={isDisabled} />
          {data && includeMenu && showActionMenu && (
            <ResourceOptionMenu prefixId={data?.prefixId} includeTemplate={includeTemplate} />
          )}
        </HStack>
      )}

      {/* Pill row */}
      <Flex gap={2} flexWrap="wrap" align="center" mb={2}>
        {/* Status */}
        <CDS.BasePill cursor="default" _hover={{}} flexShrink={0}>
          <Flex direction="column" gap="0">
            <Text fontSize="10px" color="content.tertiary" fontWeight="500" lineHeight="1.3">
              Status
            </Text>
            <HStack spacing="1" align="center">
              <Box
                w="6px"
                h="6px"
                borderRadius="full"
                bg={MESSAGE_STATUS_COLORS[data.status] ?? "gray.400"}
                flexShrink={0}
              />
              <Text fontSize="12px" fontWeight="700" color="content.primary" lineHeight="1.3" whiteSpace="nowrap">
                {MESSAGE_STATUS_LABELS[data.status ?? ""] ?? titleize(data.status ?? "")}
              </Text>
            </HStack>
          </Flex>
        </CDS.BasePill>

        {/* Scheduled */}
        <Box flexShrink={0}>
          <SendAtFieldTag message={data} />
        </Box>
        <Box flexShrink={0}>
          <TriggerFieldTag message={data} />
        </Box>

        {/* Onboarding Journey — last in the row */}
        {includeJourney && data.newHireJourneyId && (
          <NewHireStackedLabel journeyId={data.newHireJourneyId} emphasized />
        )}
      </Flex>

      {isPending ? (
        <ListIssuesByActionDuringPending prefixId={data?.prefixId} align="flex-start" />
      ) : (
        <ListIssuesByAction prefixId={data?.prefixId} align="flex-start" />
      )}
      <Divider my="4" />

      {/* Send from */}
      <HStack spacing={3} align="center" mb={3}>
        <Text fontSize="sm" fontWeight="medium" color="fg.muted" w="20" flexShrink={0}>
          Send from
        </Text>
        <SenderSelect form={form} isDisabled={isDisabled} originalSenderId={data.senderId} />
      </HStack>

      <ComposeBox>
        <ComposeBox.HeaderRow label="To">
          <RecipientPicker form={form} data={data} isDisabled={isDisabled} />
        </ComposeBox.HeaderRow>
        <Box
          px={4}
          pt={3}
          pb={2}
          bg={composeBodyBg}
          transition="background-color 0.15s"
          sx={{
            ...BORDERLESS_TRIX_SX,
            "&:hover:not(:focus-within)": { bg: composeBodyHoverBg },
          }}
        >
          <TrixEditor control={form.control} name="body" alwaysShowToolbar isEditable={!isDisabled} hideHeading placeholder="Write the message…" />
        </Box>
      </ComposeBox>
    </VStack>
  );
}
